import { useQuery, useMutation, keepPreviousData } from '@tanstack/react-query';
import { isNotNil } from 'es-toolkit';
import { AKBAPI } from '@newo/api/gateway';
import type {
    ITopic,
    ITopicData,
    ITopicsParams,
    IAKBTasksParams
} from '@newo/api/gateway';
import axios from '@/utils/api/axiosInstance';

const akbAPI = new AKBAPI(axios);

type IQueryKeys = {
    getTopics: (params: ITopicsParams) => [string, ITopicsParams];
    getTasks: (params: IAKBTasksParams) => [string, IAKBTasksParams];
};

export const QUERY_KEYS: IQueryKeys = {
    getTopics: (params) => ['akb/topics', params],
    getTasks: (params) => ['akb/tasks', params]
};

// GET topics
export const useTopicsQuery = (params: ITopicsParams) => {
    return useQuery({
        queryKey: QUERY_KEYS.getTopics(params),
        queryFn: async () => {
            const processed = Object.entries(params).reduce<
                Record<string, unknown>
            >((accumulator, [key, value]) => {
                if (isNotNil(value) && String(value).trim() !== '') {
                    accumulator[key] = value;
                }
                return accumulator;
            }, {});
            return akbAPI.getTopics(processed);
        },
        enabled: Boolean(params.persona_id),
        placeholderData: keepPreviousData
    });
};

// CREATE Topic
export const useCreateTopicMutation = () => {
    return useMutation({
        mutationFn: async (data: ITopicData) => akbAPI.createTopic(data)
    });
};

// UPDATE Topic
export const useUpdateTopicMutation = () => {
    return useMutation({
        mutationFn: async ({
            topicId,
            data
        }: {
            topicId: string;
            data: ITopicData;
        }) => akbAPI.updateTopic(topicId, data)
    });
};

// DELETE Topic
export const useDeleteTopicMutation = () => {
    return useMutation({
        mutationFn: async ({ topicId }: { topicId: string }) =>
            akbAPI.deleteTopic(topicId)
    });
};

// DELETE Topics
export const useDeleteTopicsMutation = () => {
    return useMutation({
        mutationFn: async (data: { topic_ids: string[] }) =>
            akbAPI.deleteTopics(data)
    });
};

// DELETE Persona AKB
export const useDeletePersonaAKBMutation = () => {
    return useMutation({
        mutationFn: async ({ personaId }: { personaId: string }) =>
            akbAPI.deletePersonaAKB(personaId)
    });
};

// EXPORT akb
export const useExportAKBMutation = () => {
    return useMutation({
        mutationFn: async (params: { persona_id: string }) =>
            akbAPI.exportAKB(params)
    });
};

// IMPORT akb
export const useImportAKBMutation = () => {
    return useMutation({
        mutationFn: async ({
            personaId,
            data
        }: {
            personaId: string;
            data: ITopic[];
        }) => akbAPI.importAKB(personaId, data)
    });
};

// Import AKB from text
export const useImportAKBFileMutation = () => {
    return useMutation({
        mutationFn: async ({
            personaId,
            data
        }: {
            personaId: string;
            data: FormData;
        }) => akbAPI.importAKBFile(personaId, data)
    });
};

// GET tasks
export const useAKBTasksQuery = (params: IAKBTasksParams) => {
    return useQuery({
        queryKey: QUERY_KEYS.getTasks(params),
        queryFn: async () => akbAPI.getAKBTasks(params),
        enabled: Boolean(params.persona_id)
    });
};
